import React, { useState, useEffect, useCallback, useRef } from 'react';
import { db, auth, default as app } from '../firebase';
import { doc, setDoc, serverTimestamp, collection, where, query, getDocs } from 'firebase/firestore';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from "firebase/functions";
import { Form, Button, Container, Row, Col, Alert, ProgressBar, ListGroup } from 'react-bootstrap';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

// 47都道府県のリスト（バリデーション用）
const PREFECTURES = [
  '北海道', '青森県', '岩手県', '宮城県', '秋田県', '山形県', '福島県',
  '茨城県', '栃木県', '群馬県', '埼玉県', '千葉県', '東京都', '神奈川県',
  '新潟県', '富山県', '石川県', '福井県', '山梨県', '長野県', '岐阜県',
  '静岡県', '愛知県', '三重県', '滋賀県', '京都府', '大阪府', '兵庫県',
  '奈良県', '和歌山県', '鳥取県', '島根県', '岡山県', '広島県', '山口県',
  '徳島県', '香川県', '愛媛県', '高知県', '福岡県', '佐賀県', '長崎県',
  '熊本県', '大分県', '宮崎県', '鹿児島県', '沖縄県'
];

const calculateAddressLength = (address) => {
  const fullAddress = `${address.address1 || ''}${address.address2 || ''}${address.address3 || ''}`;
  let length = 0;
  for (let i = 0; i < fullAddress.length; i++) {
    length += fullAddress.charCodeAt(i).toString(16).length > 2 ? 2 : 1;
  }
  return length;
};

const AddressLengthIndicator = ({ address }) => {
  const MAX_ADDRESS_LENGTH = 150;
  const length = calculateAddressLength(address);
  const variant = length > MAX_ADDRESS_LENGTH ? 'danger' : 'success';
  return (
    <div className="mt-1">
      <ProgressBar now={length} max={MAX_ADDRESS_LENGTH} variant={variant} />
      <small className={`d-block text-end ${variant === 'danger' ? 'text-danger' : 'text-muted'}`}>
        文字数: {length} / {MAX_ADDRESS_LENGTH} (半角1文字、全角2文字で計算)
      </small>
    </div>
  );
};

function Signup() {
  const [formData, setFormData] = useState({
    personName: '', email: '', phone: '', zipCode: '', address1: '',
    address2: '', address3: '', userAttribute: '一般ユーザー',
    userAttributeCode: '0001', companyName: '', companyPrefix: '',
    companySuffix: '', branchName: '', departmentName: '',
    isSoleProprietor: false,
  });

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [phoneParts, setPhoneParts] = useState({ part1: '', part2: '', part3: '' });
  
  const [allDepartmentSuggestions, setAllDepartmentSuggestions] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionRef = useRef(null);

  useEffect(() => {
    document.title = '新規登録 | My Order System';
  }, []);

  useEffect(() => {
    const { part1, part2, part3 } = phoneParts;
    if (part1 && part2 && part3) {
      setFormData(prev => ({ ...prev, phone: `${part1}-${part2}-${part3}` }));
    } else {
      setFormData(prev => ({ ...prev, phone: '' }));
    }
  }, [phoneParts]);

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');
    setPhoneParts(prev => ({ ...prev, [name]: sanitizedValue }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'zipCode') {
      // 郵便番号：数字以外をリアルタイムで削除
      const sanitizedValue = value.replace(/[^0-9]/g, '');
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
      
      // 文字数が7桁未満になったらエラー表示を消す
      if (sanitizedValue.length < 7) {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.zipCode;
          return newErrors;
        });
      }
    } else if (name === 'address1') {
      // 都道府県：リアルタイム・バリデーション
      setFormData(prev => ({ ...prev, [name]: value }));
      if (value.length > 0 && !PREFECTURES.includes(value)) {
        setFormErrors(prev => ({ ...prev, address1: '正しい都道府県名を入力してください（例: 東京都）' }));
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.address1;
          return newErrors;
        });
      }
    } else if (name === 'companyName') {
      const forbiddenKeywords = ['株式会社', '有限会社', '合同会社', '合名会社', '合資会社', '財団法人', '社団法人', '医療法人', '学校法人', '宗教法人', 'NPO法人'];
      const hasForbiddenKeyword = forbiddenKeywords.some(keyword => value.includes(keyword));
      if (hasForbiddenKeyword) {
        setFormErrors(prev => ({ ...prev, [name]: '法人格は入力できません' }));
      } else {
        setFormErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
      setFormData(prev => ({ ...prev, [name]: value }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }

    // 部署名入力時のリアルタイム絞り込み処理
    if (name === 'departmentName') {
      if (value.length > 0) {
        const filtered = allDepartmentSuggestions.filter(dept => dept.includes(value));
        setFilteredDepartments(filtered);
        setShowSuggestions(true);
      } else {
        setFilteredDepartments(allDepartmentSuggestions);
        setShowSuggestions(allDepartmentSuggestions.length > 0);
      }
    }
  };

  // 【新規追加】郵便番号が7桁になったら自動でAPIを叩いて住所を取得する
  useEffect(() => {
    const fetchAddress = async () => {
      if (formData.zipCode.length === 7) {
        try {
          const res = await axios.get('https://zipcloud.ibsnet.co.jp/api/search', { params: { zipcode: formData.zipCode } });
          if (res.data.results) {
            const result = res.data.results[0];
            setFormData(prev => ({ 
              ...prev, 
              address1: result.address1, 
              address2: result.address2 + result.address3 
            }));
            // 正常に取得できた場合、関連するエラーを消去
            setFormErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors.zipCode;
              delete newErrors.address1;
              return newErrors;
            });
          } else {
            setFormErrors(prev => ({ ...prev, zipCode: '該当する住所が見つかりませんでした。' }));
          }
        } catch (err) {
          console.error("住所の取得に失敗しました", err);
          setFormErrors(prev => ({ ...prev, zipCode: '住所の検索に失敗しました。' }));
        }
      }
    };

    fetchAddress();
  }, [formData.zipCode]);

  useEffect(() => {
    const fetchDepartments = async () => {
      const { companyName, branchName } = formData;
      if (companyName && branchName) {
        try {
          const companiesRef = collection(db, "companies");
          const q = query(
            companiesRef, 
            where("companyName", "==", companyName),
            where("branchName", "==", branchName)
          );
          const querySnapshot = await getDocs(q);
          
          const departments = new Set();
          querySnapshot.forEach((doc) => {
            const data = doc.data();
            if (data.departmentName) {
              departments.add(data.departmentName);
            }
          });
          
          const deptArray = Array.from(departments);
          setAllDepartmentSuggestions(deptArray);
          setFilteredDepartments(deptArray); 
        } catch (error) {
          console.error("部署名の取得に失敗しました", error);
        }
      } else {
        setAllDepartmentSuggestions([]);
        setFilteredDepartments([]);
      }
    };

    fetchDepartments();
  }, [formData.companyName, formData.branchName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [suggestionRef]);

  const handleSuggestionClick = (deptName) => {
    setFormData(prev => ({ ...prev, departmentName: deptName }));
    setShowSuggestions(false);
  };

  useEffect(() => {
    if (formData.email.endsWith('@seko.co.jp')) {
      setFormData(prev => ({ ...prev, userAttribute: '社員', userAttributeCode: '1111' }));
    } else {
      let code = '0001';
      if (formData.companyName.includes('積水ハウス')) code = '1001';
      else if (formData.companyName.includes('旭化成')) code = '0101';
      else if (formData.companyName.includes('住友林業')) code = '0011';
      setFormData(prev => ({ ...prev, userAttribute: '一般ユーザー', userAttributeCode: code }));
    }
  }, [formData.email, formData.companyName]);
  
  useEffect(() => { if (formData.companyPrefix) { setFormData(prev => ({ ...prev, companySuffix: '' })); } }, [formData.companyPrefix]);
  useEffect(() => { if (formData.companySuffix) { setFormData(prev => ({ ...prev, companyPrefix: '' })); } }, [formData.companySuffix]);
  useEffect(() => { if (formData.isSoleProprietor) { setFormData(prev => ({ ...prev, companyPrefix: '', companySuffix: '' })); } }, [formData.isSoleProprietor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    
    // リアルタイムバリデーションのエラーが残っている場合は登録をブロック
    if (formErrors.address1) { setError('住所1（都道府県）の入力内容にエラーがあります。'); return; }
    if (formErrors.zipCode) { setError('郵便番号を確認してください。'); return; }

    if (!formData.email) { setError('メールアドレスは必須です。'); return; }
    if (!formData.companyName && !formData.isSoleProprietor) { setError('企業名を入力してください。'); return; }
    if (!formData.phone) { setError('電話番号を正しく入力してください。'); return; }
    
    // 念のための最終チェック
    if (formData.address1 && !PREFECTURES.includes(formData.address1)) {
      setError('住所1（都道府県）には正しい都道府県名を入力してください。（例: 東京都、愛知県 など）');
      return; 
    }

    if (calculateAddressLength(formData) > 150) {
      setError('住所の合計文字数が上限を超えています。');
      return;
    }
    
    try {
      const tempPassword = Math.random().toString(36).slice(-8);
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, tempPassword);
      const user = userCredential.user;

      let companyIdToSet;
      const companiesRef = collection(db, "companies");
      const specialCompanies = ["積水ハウス", "旭化成", "住友林業"];
      const enteredCompanyName = formData.companyName;
      const foundSpecialCompany = specialCompanies.find(name => enteredCompanyName.includes(name));
      const companyNameToSearch = foundSpecialCompany || enteredCompanyName;
      
      const q = query(
        companiesRef, 
        where("companyName", "==", companyNameToSearch),
        where("branchName", "==", formData.branchName),
        where("departmentName", "==", formData.departmentName)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        companyIdToSet = querySnapshot.docs[0].id;
      } else {
        companyIdToSet = uuidv4();
        const companyData = {
          companyId: companyIdToSet,
          companyName: companyNameToSearch,
          companyPrefix: formData.companyPrefix,
          companySuffix: formData.companySuffix,
          branchName: formData.branchName,
          departmentName: formData.departmentName,
          isSoleProprietor: formData.isSoleProprietor,
          createdAt: serverTimestamp(),
        };
        await setDoc(doc(db, 'companies', companyIdToSet), companyData);
      }

      const userData = {
        uid: user.uid,
        personName: formData.personName,
        email: formData.email,
        phone: formData.phone,
        zipCode: formData.zipCode,
        address1: formData.address1,
        address2: formData.address2,
        address3: formData.address3,
        userAttribute: formData.userAttribute,
        userAttributeCode: formData.userAttributeCode,
        companyId: companyIdToSet,
        status: 'active',
        createdAt: serverTimestamp(),
      };

      await setDoc(doc(db, 'users', user.uid), userData);

      // const actionCodeSettings = {
      //   url: 'https://sample-order-system.xyz/login',
      //   handleCodeInApp: true,
      // };

      const actionCodeSettings = {
        // local
        // url: 'localhost:3000/login',
        // url: 'https://sample-order-system.xyz/login',
        // url: 'https://seko-sample-dev.firebaseapp.com/login',
        url: 'https://samplecenter.seko.co.jp/login',
        // 環境変数ファイル(.env)から自動で読み込む
        // url: process.env.REACT_APP_LOGIN_URL,
        handleCodeInApp: true,
      };
      await sendPasswordResetEmail(auth, formData.email, actionCodeSettings);

      setMessage('登録ありがとうございます。パスワード設定用のURLを記載したメールを送信しましたので、ご確認ください。');

      try {
        const functions = getFunctions(app, 'asia-northeast1');
        const notifyAdmins = httpsCallable(functions, 'notifyAdminsOfNewUser');
        await notifyAdmins({
          personName: formData.personName,
          email: formData.email,
          companyName: formData.companyName,
          branchName: formData.branchName,
          departmentName: formData.departmentName,
          userAttribute: formData.userAttribute,
        });
      } catch (notificationError) {
        console.error("管理者への通知に失敗しました:", notificationError);
      }

    } catch (err) {
      console.error("登録エラー:", err);
      let errorMessage = `登録に失敗しました: ${err.message}`;
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'このメールアドレスは既に使用されています。';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'メールアドレスの形式が正しくありません。';
      }
      setError(errorMessage);
    }
  };

  return (
    <Container className="my-5">
      <Row className="justify-content-md-center">
        <Col md={10} lg={8}>
          <h2>
            ユーザー新規登録
          </h2>
          <Form onSubmit={handleSubmit}>
            {
              message &&
              <Alert variant="success">
                {message}
              </Alert>
            }
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <h5 className="mt-4">
              ユーザー情報
            </h5>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                氏名
              </Form.Label>
              <Col sm={9}>
                <Form.Control type="text" name="personName" value={formData.personName} onChange={handleChange} required />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                メールアドレス
              </Form.Label>
              <Col sm={9}>
                <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                電話番号
              </Form.Label>
              <Col sm={9}>
                <Row className="g-2 align-items-center">
                  <Col>
                    <Form.Control type="tel" name="part1" value={phoneParts.part1} onChange={handlePhoneChange} maxLength="5" placeholder="090" />
                  </Col>
                  <Col xs="auto">
                    -
                  </Col>
                  <Col>
                    <Form.Control type="tel" name="part2" value={phoneParts.part2} onChange={handlePhoneChange} maxLength="4" placeholder="1234" />
                  </Col>
                  <Col xs="auto">
                    -
                  </Col>
                  <Col>
                    <Form.Control type="tel" name="part3" value={phoneParts.part3} onChange={handlePhoneChange} maxLength="4" placeholder="5678" />
                  </Col>
                </Row>
              </Col>
            </Form.Group>

            {/* 郵便番号：7桁で自動検索＆リアルタイムエラー表示 */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}
              >郵便番号 (ハイフンなし)
              </Form.Label>
              <Col sm={9}>
                <Form.Control 
                  type="text" 
                  name="zipCode" 
                  value={formData.zipCode} 
                  onChange={handleChange} 
                  maxLength="7" 
                  placeholder="例: 1234567 （7桁入力で自動で住所を入力します）" 
                  isInvalid={!!formErrors.zipCode}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.zipCode}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            {/* 住所1：リアルタイムバリデーション */}
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                住所1 (都道府県)
              </Form.Label>
              <Col sm={9}>
                <Form.Control 
                  type="text" 
                  name="address1" 
                  value={formData.address1} 
                  onChange={handleChange} 
                  placeholder="例: 東京都" 
                  isInvalid={!!formErrors.address1}
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.address1}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                住所2 (市区町村以降)
              </Form.Label>
              <Col sm={9}>
                <Form.Control type="text" name="address2" value={formData.address2} onChange={handleChange} />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                住所3 (ビル名・マンション名)
              </Form.Label>
              <Col sm={9}>
                <Form.Control type="text" name="address3" value={formData.address3} onChange={handleChange} placeholder="ビル名・マンション名、部屋番号などを入力" />
              </Col>
            </Form.Group>
            <Row className="mb-3">
              <Col sm={{ span: 9, offset: 3 }}>
                <AddressLengthIndicator address={formData} />
              </Col>
            </Row>

            <h5 className="mt-5">
              企業情報
            </h5>
            <Form.Group className="mb-3">
              <Form.Check type="checkbox" name="isSoleProprietor" label="個人事業主の場合はチェック" checked={formData.isSoleProprietor} onChange={handleChange} />
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                企業名
                {
                  formErrors.companyName &&
                  <small className="text-danger ms-2">
                    {formErrors.companyName}
                  </small>
                }
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="法人格を除いた正式名称で入力（例: 旭興西武）"
                  required={!formData.isSoleProprietor}
                  isInvalid={!!formErrors.companyName}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                法人格
              </Form.Label>
              <Col sm={4}>
                <Form.Select name="companyPrefix" value={formData.companyPrefix} onChange={handleChange} disabled={formData.isSoleProprietor}>
                  <option value="">
                    (前株など)
                  </option>
                  <option value="株式会社">
                    株式会社
                  </option>
                  <option value="有限会社">
                    有限会社
                  </option>
                  <option value="合同会社">
                    合同会社
                  </option>
                  <option value="合名会社">
                    合名会社
                  </option>
                  <option value="合資会社">
                    合資会社
                  </option>
                </Form.Select>
              </Col>
              <Col sm={4}>
                <Form.Select name="companySuffix" value={formData.companySuffix} onChange={handleChange} disabled={formData.isSoleProprietor}>
                  <option value="">
                    (後株など)
                  </option>
                  <option value="株式会社">
                    株式会社
                  </option>
                  <option value="有限会社">
                    有限会社
                  </option>
                  <option value="合同会社">
                    合同会社
                  </option>
                  <option value="合名会社">
                    合名会社
                  </option>
                  <option value="合資会社">
                    合資会社
                  </option>
                </Form.Select>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                支店名
              </Form.Label>
              <Col sm={9}>
                <Form.Control type="text" name="branchName" value={formData.branchName} onChange={handleChange} />
              </Col>
            </Form.Group>
            
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                部署名
              </Form.Label>
              <Col sm={9} ref={suggestionRef} style={{ position: 'relative' }}>
                <Form.Control 
                  type="text" 
                  name="departmentName" 
                  value={formData.departmentName} 
                  onChange={handleChange} 
                  onFocus={() => {
                    if (filteredDepartments.length > 0) {
                      setShowSuggestions(true);
                    }
                  }}
                  placeholder="部署名を入力、または候補から選択"
                  autoComplete="off" 
                />
                {showSuggestions && filteredDepartments.length > 0 && (
                  <ListGroup 
                    style={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: '12px',
                      right: '12px', 
                      zIndex: 1000, 
                      maxHeight: '200px', 
                      overflowY: 'auto',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}
                  >
                    {filteredDepartments.map((dept, index) => (
                      <ListGroup.Item 
                        key={index} 
                        action 
                        onClick={() => handleSuggestionClick(dept)}
                      >
                        {dept}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Col>
            </Form.Group>

            {
              message &&
              <Alert variant="success">
                {message}
              </Alert>
            }
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <div className="d-grid gap-2 mt-4">
              <Button variant="primary" type="submit" size="lg">
                登録
              </Button>
            </div>
          </Form>
        </Col>
      </Row>
    </Container>
  );
}

export default Signup;

// import React, { useState, useEffect, useCallback } from 'react';
// import { db, auth, default as app } from '../firebase';
// import { doc, setDoc, serverTimestamp, collection, where, query, getDocs } from 'firebase/firestore';
// import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
// import { getFunctions, httpsCallable } from "firebase/functions";
// import { Form, Button, Container, Row, Col, Alert, ProgressBar } from 'react-bootstrap';
// import axios from 'axios';
// import { v4 as uuidv4 } from 'uuid';

// // 【新規追加】住所の文字数を計算するヘルパー関数
// const calculateAddressLength = (address) => {
//   const fullAddress = `${address.address1 || ''}${address.address2 || ''}${address.address3 || ''}`;
//   let length = 0;
//   for (let i = 0; i < fullAddress.length; i++) {
//     // 半角文字は1、全角文字は2としてカウント
//     length += fullAddress.charCodeAt(i).toString(16).length > 2 ? 2 : 1;
//   }
//   return length;
// };

// // 【新規追加】文字数カウンターとプログレスバーを表示するコンポーネント
// const AddressLengthIndicator = ({ address }) => {
//   const MAX_ADDRESS_LENGTH = 150;
//   const length = calculateAddressLength(address);
//   const variant = length > MAX_ADDRESS_LENGTH ? 'danger' : 'success';
//   return (
//     <div className="mt-1">
//       <ProgressBar
//         now={length}
//         max={MAX_ADDRESS_LENGTH}
//         variant={variant}
//       />
//       <small
//         className={
//           `d-block text-end 
//         ${variant === 'danger' ?
//             'text-danger'
//             :
//             'text-muted'
//           }`
//         }
//       >
//         文字数: {length} / {MAX_ADDRESS_LENGTH} (半角1文字、全角2文字で計算)
//       </small>
//     </div>
//   );
// };

// function Signup() {
//   const [formData, setFormData] = useState({
//     personName: '', email: '', phone: '', zipCode: '', address1: '',
//     address2: '', address3: '', userAttribute: '一般ユーザー',
//     userAttributeCode: '0001', companyName: '', companyPrefix: '',
//     companySuffix: '', branchName: '', departmentName: '',
//     isSoleProprietor: false,
//   });

//   const [message, setMessage] = useState('');
//   const [error, setError] = useState('');
//   const [formErrors, setFormErrors] = useState({});

//   // 【変更点】3分割された電話番号のstate
//   const [phoneParts, setPhoneParts] = useState({ part1: '', part2: '', part3: '' });

//   useEffect(() => {
//     document.title = '新規登録 | My Order System';
//   }, []);

//   // 【変更点】3つのパーツを結合して、メインのphone stateを更新
//   useEffect(() => {
//     const { part1, part2, part3 } = phoneParts;
//     // 全てのパーツが入力されたらハイフン付きで結合
//     if (part1 && part2 && part3) {
//       setFormData(prev => ({ ...prev, phone: `${part1}-${part2}-${part3}` }));
//     } else {
//       // 入力途中は空にするか、部分的に結合するか選択（今回は空にする）
//       setFormData(prev => ({ ...prev, phone: '' }));
//     }
//   }, [phoneParts]);

//   // 【変更点】電話番号専用の入力ハンドラ
//   const handlePhoneChange = (e) => {
//     const { name, value } = e.target;
//     // 数字以外の入力を防ぐ
//     const sanitizedValue = value.replace(/[^0-9]/g, '');
//     setPhoneParts(prev => ({ ...prev, [name]: sanitizedValue }));
//   };

//   const handleChange = (e) => {
//     const { name, value, type, checked } = e.target;

//     if (name === 'companyName') {
//       const forbiddenKeywords = ['株式会社', '有限会社', '合同会社', '合名会社', '合資会社', '財団法人', '社団法人', '医療法人', '学校法人', '宗教法人', 'NPO法人'];
//       const hasForbiddenKeyword = forbiddenKeywords.some(keyword => value.includes(keyword));
//       if (hasForbiddenKeyword) {
//         setFormErrors(prev => ({ ...prev, [name]: '法人格は入力できません' }));
//       } else {
//         setFormErrors(prev => {
//           const newErrors = { ...prev };
//           delete newErrors[name];
//           return newErrors;
//         });
//       }
//       setFormData(prev => ({ ...prev, [name]: value }));
//     } else {
//       setFormData(prev => ({
//         ...prev,
//         [name]: type === 'checkbox' ? checked : value,
//       }));
//     }
//   };

//   useEffect(() => {
//     if (formData.email.endsWith('@seko.co.jp')) {
//       setFormData(prev => ({ ...prev, userAttribute: '社員', userAttributeCode: '1111' }));
//     } else {
//       let code = '0001';
//       if (formData.companyName.includes('積水ハウス')) code = '1001';
//       else if (formData.companyName.includes('旭化成')) code = '0101';
//       else if (formData.companyName.includes('住友林業')) code = '0011';
//       setFormData(prev => ({ ...prev, userAttribute: '一般ユーザー', userAttributeCode: code }));
//     }
//   }, [formData.email, formData.companyName]);
//   useEffect(() => { if (formData.companyPrefix) { setFormData(prev => ({ ...prev, companySuffix: '' })); } }, [formData.companyPrefix]);
//   useEffect(() => { if (formData.companySuffix) { setFormData(prev => ({ ...prev, companyPrefix: '' })); } }, [formData.companySuffix]);
//   useEffect(() => { if (formData.isSoleProprietor) { setFormData(prev => ({ ...prev, companyPrefix: '', companySuffix: '' })); } }, [formData.isSoleProprietor]);
//   const handlePostalCodeSearch = useCallback(async () => { if (formData.zipCode.length === 7) { try { const res = await axios.get('https://zipcloud.ibsnet.co.jp/api/search', { params: { zipcode: formData.zipCode } }); if (res.data.results) { const result = res.data.results[0]; setFormData(prev => ({ ...prev, address1: result.address1, address2: result.address2 + result.address3, address3: '' })); } else { alert('該当する住所が見つかりませんでした。'); } } catch (err) { console.error("住所の取得に失敗しました", err); alert('住所の自動入力に失敗しました。'); } } }, [formData.zipCode]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setMessage('');
//     setError('');
//     if (!formData.email) { setError('メールアドレスは必須です。'); return; }
//     if (!formData.companyName && !formData.isSoleProprietor) { setError('企業名を入力してください。'); return; }
//     // 【変更点】電話番号が正しく結合されているかチェック
//     if (!formData.phone) { setError('電話番号を正しく入力してください。'); return; }
//     if (calculateAddressLength(formData) > 150) {
//       setError('住所の合計文字数が上限を超えています。');
//       return;
//     }
//     try {
//       const tempPassword = Math.random().toString(36).slice(-8);
//       const userCredential = await createUserWithEmailAndPassword(auth, formData.email, tempPassword);
//       const user = userCredential.user;

//       let companyIdToSet;
//       const companiesRef = collection(db, "companies");
//       const specialCompanies = ["積水ハウス", "旭化成", "住友林業"];
//       const enteredCompanyName = formData.companyName;
//       const foundSpecialCompany = specialCompanies.find(name => enteredCompanyName.includes(name));
//       const companyNameToSearch = foundSpecialCompany || enteredCompanyName;
//       const q = query(
//         companiesRef, 
//         where("companyName", "==", companyNameToSearch),
//         where("branchName", "==", formData.branchName),
//         where("departmentName", "==", formData.departmentName)
//       );
//       const querySnapshot = await getDocs(q);

//       if (!querySnapshot.empty) {
//         companyIdToSet = querySnapshot.docs[0].id;
//       } else {
//         companyIdToSet = uuidv4();
//         const companyData = {
//           companyId: companyIdToSet,
//           companyName: companyNameToSearch,
//           companyPrefix: formData.companyPrefix,
//           companySuffix: formData.companySuffix,
//           branchName: formData.branchName,
//           departmentName: formData.departmentName,
//           isSoleProprietor: formData.isSoleProprietor,
//           createdAt: serverTimestamp(),
//         };
//         await setDoc(doc(db, 'companies', companyIdToSet), companyData);
//       }

//       const userData = {
//         uid: user.uid,
//         personName: formData.personName,
//         email: formData.email,
//         phone: formData.phone, // ハイフン付きの電話番号が保存される
//         zipCode: formData.zipCode,
//         address1: formData.address1,
//         address2: formData.address2,
//         address3: formData.address3,
//         userAttribute: formData.userAttribute,
//         userAttributeCode: formData.userAttributeCode,
//         companyId: companyIdToSet,
//         status: 'active',
//         createdAt: serverTimestamp(),
//       };

//       await setDoc(doc(db, 'users', user.uid), userData);

//       const actionCodeSettings = {
//         // local
//         // url: 'localhost:3000/login',
//         url: 'https://sample-order-system.xyz/login',
//         // url: 'https://seko-sample-dev.firebaseapp.com/login',
//         // url: 'https://samplecenter.seko.co.jp/login',
//         // 環境変数ファイル(.env)から自動で読み込む
//         // url: process.env.REACT_APP_LOGIN_URL,
//         handleCodeInApp: true,
//       };
//       await sendPasswordResetEmail(auth, formData.email, actionCodeSettings);

//       setMessage('登録ありがとうございます。パスワード設定用のURLを記載したメールを送信しましたので、ご確認ください。');

//       try {
//         const functions = getFunctions(app, 'asia-northeast1');
//         const notifyAdmins = httpsCallable(functions, 'notifyAdminsOfNewUser');
//         await notifyAdmins({
//           personName: formData.personName,
//           email: formData.email,
//           companyName: formData.companyName,
//           branchName: formData.branchName,
//           departmentName: formData.departmentName,
//           userAttribute: formData.userAttribute,
//         });
//       } catch (notificationError) {
//         console.error("管理者への通知に失敗しました:", notificationError);
//       }

//     } catch (err) {
//       console.error("登録エラー:", err);
//       let errorMessage = `登録に失敗しました: ${err.message}`;
//       if (err.code === 'auth/email-already-in-use') {
//         errorMessage = 'このメールアドレスは既に使用されています。';
//       } else if (err.code === 'auth/invalid-email') {
//         errorMessage = 'メールアドレスの形式が正しくありません。';
//       }
//       setError(errorMessage);
//     }
//   };

//   return (
//     <Container className="my-5">
//       <Row className="justify-content-md-center">
//         <Col md={10} lg={8}>
//           <h2>
//             ユーザー新規登録
//           </h2>
//           <Form onSubmit={handleSubmit}>
//             {
//               message &&
//               <Alert variant="success">
//                 {message}
//               </Alert>
//             }
//             {
//               error &&
//               <Alert variant="danger">
//                 {error}
//               </Alert>
//             }
//             <h5 className="mt-4">
//               ユーザー情報
//             </h5>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 氏名
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="text" name="personName" value={formData.personName} onChange={handleChange} required />
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 メールアドレス
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
//               </Col>
//             </Form.Group>

//             {/* 【変更点】電話番号の入力欄を3分割に変更 */}
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 電話番号
//               </Form.Label>
//               <Col sm={9}>
//                 <Row className="g-2 align-items-center">
//                   <Col>
//                     <Form.Control type="tel" name="part1" value={phoneParts.part1} onChange={handlePhoneChange} maxLength="5" placeholder="090" />
//                   </Col>
//                   <Col xs="auto">
//                     -
//                   </Col>
//                   <Col>
//                     <Form.Control type="tel" name="part2" value={phoneParts.part2} onChange={handlePhoneChange} maxLength="4" placeholder="1234" />
//                   </Col>
//                   <Col xs="auto">
//                     -
//                   </Col>
//                   <Col>
//                     <Form.Control type="tel" name="part3" value={phoneParts.part3} onChange={handlePhoneChange} maxLength="4" placeholder="5678" />
//                   </Col>
//                 </Row>
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}
//               >郵便番号 (ハイフンなし)
//               </Form.Label><Col sm={5}>
//                 <Form.Control type="text" name="zipCode" value={formData.zipCode} onChange={handleChange} maxLength="7" />
//               </Col>
//               <Col sm={4}>
//                 <Button variant="secondary" onClick={handlePostalCodeSearch}>
//                   住所自動入力
//                 </Button>
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 住所1 (都道府県)
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="text" name="address1" value={formData.address1} onChange={handleChange} />
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 住所2 (市区町村以降)
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="text" name="address2" value={formData.address2} onChange={handleChange} />
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 住所3 (ビル名・マンション名)
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="text" name="address3" value={formData.address3} onChange={handleChange} placeholder="ビル名・マンション名、部屋番号などを入力" />
//               </Col>
//             </Form.Group>
//             <Row className="mb-3">
//               <Col sm={{ span: 9, offset: 3 }}>
//                 <AddressLengthIndicator address={formData} />
//               </Col>
//             </Row>
//             <h5 className="mt-5">
//               企業情報
//             </h5>
//             <Form.Group className="mb-3">
//               <Form.Check type="checkbox" name="isSoleProprietor" label="個人事業主の場合はチェック" checked={formData.isSoleProprietor} onChange={handleChange} />
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 企業名
//                 {
//                   formErrors.companyName &&
//                   <small className="text-danger ms-2">
//                     {formErrors.companyName}
//                   </small>
//                 }
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control
//                   type="text"
//                   name="companyName"
//                   value={formData.companyName}
//                   onChange={handleChange}
//                   placeholder="法人格を除いた正式名称で入力（例: 旭興西武）"
//                   required={!formData.isSoleProprietor}
//                   isInvalid={!!formErrors.companyName}
//                 />
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 法人格
//               </Form.Label>
//               <Col sm={4}>
//                 <Form.Select name="companyPrefix" value={formData.companyPrefix} onChange={handleChange} disabled={formData.isSoleProprietor}>
//                   <option value="">
//                     (前株など)
//                   </option>
//                   <option value="株式会社">
//                     株式会社
//                   </option>
//                   <option value="有限会社">
//                     有限会社
//                   </option>
//                   <option value="合同会社">
//                     合同会社
//                   </option>
//                   <option value="合名会社">
//                     合名会社
//                   </option>
//                   <option value="合資会社">
//                     合資会社
//                   </option>
//                 </Form.Select>
//               </Col>
//               <Col sm={4}>
//                 <Form.Select name="companySuffix" value={formData.companySuffix} onChange={handleChange} disabled={formData.isSoleProprietor}>
//                   <option value="">
//                     (後株など)
//                   </option>
//                   <option value="株式会社">
//                     株式会社
//                   </option>
//                   <option value="有限会社">
//                     有限会社
//                   </option>
//                   <option value="合同会社">
//                     合同会社
//                   </option>
//                   <option value="合名会社">
//                     合名会社
//                   </option>
//                   <option value="合資会社">
//                     合資会社
//                   </option>
//                 </Form.Select>
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 支店名
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="text" name="branchName" value={formData.branchName} onChange={handleChange} />
//               </Col>
//             </Form.Group>
//             <Form.Group as={Row} className="mb-3">
//               <Form.Label column sm={3}>
//                 部署名
//               </Form.Label>
//               <Col sm={9}>
//                 <Form.Control type="text" name="departmentName" value={formData.departmentName} onChange={handleChange} />
//               </Col>
//             </Form.Group>
//             {
//               message &&
//               <Alert variant="success">
//                 {message}
//               </Alert>
//             }
//             {
//               error &&
//               <Alert variant="danger">
//                 {error}
//               </Alert>
//             }
//             <div className="d-grid gap-2 mt-4">
//               <Button variant="primary" type="submit" size="lg">
//                 登録
//               </Button>
//             </div>
//           </Form>
//         </Col>
//       </Row>
//     </Container>
//   );
// }

// export default Signup;
